import { LegalLayout } from '../components/LegalLayout';
import { FileText } from 'lucide-react';

export default function TermsPage() {
  const sections = [
    {
      id: "scope",
      title: "Scope",
      content: (
        <>
          <p>
            Visainterviews.ai is a trading name of <strong>Cloud Lab Technologies Ltd</strong>, a company registered in England and Wales, with its registered office at <strong>4 Crown Place, Liverpool Street, London, EC2A 4BT</strong> ("Visainterviews.ai", "we", "us", "our").
          </p>
          <p>
            Visainterviews.ai operates a cloud-based platform enabling customers ("Users") to deploy AI-powered voice assistants ("Assistants") to automate customer interactions ("Software") and associated services offered as a subscription ("Subscription").
          </p>
          <p>
            These General Terms and Conditions ("GTC") govern the contractual relationship between Visainterviews.ai and the User.
          </p>
          <p>
            Our services are intended exclusively for <strong>business customers</strong> as defined by the UK Consumer Rights Act 2015, not for consumers.
          </p>
          <p>
            Any conflicting or supplemental terms provided by the User shall not apply unless explicitly accepted in writing by Visainterviews.ai.
          </p>
        </>
      )
    },
    {
      id: "object",
      title: "Object of the Agreement",
      content: (
        <>
          <p>
            Visainterviews.ai provides the Software as a Software-as-a-Service (SaaS) solution, operated in a managed cloud environment and accessed online.
          </p>
          <p>
            The User must register an account to access the Software. By registering, the User agrees to these GTC.
          </p>
          <p>Subscriptions may include:</p>
          <ul>
            <li><strong>Direct Use</strong> – Use of the Software for the User's own business</li>
            <li><strong>Reselling</strong> – Reselling Visainterviews.ai services under our brand to the User's customers ("End Customers")</li>
            <li><strong>White Labelling</strong> – Reselling the Software under the User's brand</li>
          </ul>
          <p>
            Each Subscription is subject to usage limits as detailed in the applicable Subscription Plan. Excess usage may incur additional fees.
          </p>
        </>
      )
    },
    {
      id: "conclusion",
      title: "Conclusion of the Agreement",
      content: (
        <>
          <p>
            Subscription plans and pricing on our Website are invitations to treat and not binding offers.
          </p>
          <p>
            The User submits a binding offer by selecting a Subscription Plan, providing billing details, and completing the checkout process.
          </p>
          <p>
            The Agreement becomes binding upon written or electronic acceptance by Visainterviews.ai.
          </p>
          <p>
            All communication regarding Subscriptions will be conducted via email to the address provided by the User.
          </p>
        </>
      )
    },
    {
      id: "direct-use",
      title: "Direct Use",
      content: (
        <>
          <p>
            For Direct Use, Visainterviews.ai grants a <strong>non-exclusive, non-transferable, non-sublicensable</strong> license to use the Software for the term of the Agreement.
          </p>
          <p>
            The User may create accounts for its staff as "Authorized Users".
          </p>
          <p>
            The User must not remove Visainterviews.ai's branding unless explicitly agreed.
          </p>
        </>
      )
    },
    {
      id: "reselling",
      title: "Reselling",
      content: (
        <>
          <p>
            The User may resell the Software to End Customers in their own name and for their own account.
          </p>
          <p>
            The User must ensure End Customers are bound by terms at least as strict as these GTC.
          </p>
          <p>
            The User is fully liable for End Customer breaches as if they were the User's own.
          </p>
          <p>
            All End Customer usage counts towards the User's usage limits.
          </p>
        </>
      )
    },
    {
      id: "white-labelling",
      title: "White Labelling",
      content: (
        <>
          <p>
            White Labelling rights are granted where specified in the Subscription Plan.
          </p>
          <p>
            The User may customise branding in accordance with agreed specifications.
          </p>
          <p>
            The User is solely responsible for its marketing claims, pricing, and End Customer agreements.
          </p>
        </>
      )
    },
    {
      id: "ai-disclaimer",
      title: "AI Output Disclaimer",
      content: (
        <>
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <p className="text-amber-800 dark:text-amber-200">
              <strong>Important:</strong> AI-generated responses are produced automatically and may be inaccurate or inappropriate.
            </p>
          </div>
          <p className="mt-4">
            The User is solely responsible for reviewing AI Output before relying on it.
          </p>
          <p>
            Visainterviews.ai accepts no liability for any loss, damage, or harm caused by AI Output.
          </p>
        </>
      )
    },
    {
      id: "warranty",
      title: "Warranty",
      content: (
        <>
          <p>
            Visainterviews.ai will use reasonable skill and care to provide the Software as described in the Subscription Plan.
          </p>
          <p>No warranties are given that:</p>
          <ul>
            <li>The Software will be error-free or uninterrupted</li>
            <li>AI Output will meet the User's specific expectations</li>
          </ul>
          <p>
            All other warranties, express or implied, are excluded to the fullest extent permitted by law.
          </p>
        </>
      )
    },
    {
      id: "third-party",
      title: "Third-Party Applications",
      content: (
        <>
          <p>
            The Software may integrate with third-party services (e.g., Twilio, Meta WhatsApp API, Google).
          </p>
          <p>
            Use of third-party services is subject to their own terms and conditions.
          </p>
          <p>
            Visainterviews.ai does not guarantee availability, accuracy, or support for third-party services.
          </p>
        </>
      )
    },
    {
      id: "input-output",
      title: "Input & Output",
      content: (
        <>
          <p><strong>Input:</strong> Any data provided by the User or its End Customers (including voice recordings, scripts, prompts, contact data).</p>
          <p><strong>Output:</strong> Any data generated by the Software.</p>
          <p>The User warrants that:</p>
          <ul>
            <li>All Input complies with applicable laws</li>
            <li>The User has the necessary rights to provide Input to Visainterviews.ai</li>
          </ul>
          <p>
            Visainterviews.ai may delete Input and Output after account termination unless legally required to retain it.
          </p>
        </>
      )
    },
    {
      id: "acceptable-use",
      title: "Acceptable Use & User Obligations",
      content: (
        <>
          <p>The User must not:</p>
          <ul>
            <li>Use the Software for unlawful, fraudulent, or harmful purposes</li>
            <li>Transmit spam or make nuisance calls</li>
            <li>Store or transmit malicious code</li>
            <li>Attempt unauthorized access to Visainterviews.ai systems</li>
          </ul>
          <p>
            The User is responsible for security of all account credentials.
          </p>
          <p>
            Any breach of this clause may result in immediate suspension or termination.
          </p>
        </>
      )
    },
    {
      id: "fees",
      title: "Fees & Payment",
      content: (
        <>
          <p>
            Subscription Fees are payable in advance in the currency specified on our Website.
          </p>
          <p>
            Payments must be made via the approved methods.
          </p>
          <p>
            Late payments may result in service suspension.
          </p>
          <p>
            All fees exclude VAT and other applicable taxes.
          </p>
        </>
      )
    },
    {
      id: "trials",
      title: "Trial Periods, Upgrades & Downgrades",
      content: (
        <>
          <p>
            Trial accounts are provided free for the stated period.
          </p>
          <p>
            Unless cancelled before expiry, Trials convert to paid Subscriptions automatically.
          </p>
          <p>
            Upgrades take effect immediately; downgrades take effect at the next billing cycle.
          </p>
        </>
      )
    },
    {
      id: "ip",
      title: "Intellectual Property",
      content: (
        <>
          <p>
            Visainterviews.ai retains all rights to the Software and related materials.
          </p>
          <p>
            The User retains rights to their Input.
          </p>
          <p>
            By providing Input, the User grants Visainterviews.ai a license to use it for service provision and improvement.
          </p>
        </>
      )
    },
    {
      id: "confidentiality",
      title: "Confidentiality",
      content: (
        <>
          <p>
            Both parties agree to keep confidential any non-public information disclosed during the Agreement.
          </p>
          <p>
            Confidentiality obligations survive termination for <strong>three years</strong>.
          </p>
        </>
      )
    },
    {
      id: "data-protection",
      title: "Data Protection",
      content: (
        <>
          <p>
            Visainterviews.ai processes personal data in accordance with UK GDPR.
          </p>
          <p>
            A Data Processing Agreement will be provided where applicable.
          </p>
        </>
      )
    },
    {
      id: "liability",
      title: "Liability",
      content: (
        <>
          <p>
            Visainterviews.ai's total liability in any 12-month period is limited to the total fees paid by the User in that period.
          </p>
          <p>We are not liable for:</p>
          <ul>
            <li>Loss of profits, business, data, or goodwill</li>
            <li>Any indirect or consequential losses</li>
          </ul>
          <p>
            Nothing in these terms limits liability for death or personal injury caused by negligence, fraud, or fraudulent misrepresentation.
          </p>
        </>
      )
    },
    {
      id: "termination",
      title: "Term & Termination",
      content: (
        <>
          <p>
            The Agreement continues for the Initial Term in the Subscription Plan and renews unless cancelled with at least <strong>30 days' notice</strong>.
          </p>
          <p>
            Visainterviews.ai may terminate immediately for material breach or illegal activity.
          </p>
          <p>
            On termination, all licenses end, and the User must stop using the Software.
          </p>
        </>
      )
    },
    {
      id: "changes",
      title: "Changes to Terms",
      content: (
        <>
          <p>
            Visainterviews.ai may update these GTC for legal, technical, or operational reasons.
          </p>
          <p>
            We will give at least <strong>14 days' notice</strong> of material changes.
          </p>
          <p>
            Continued use after changes means acceptance.
          </p>
        </>
      )
    },
    {
      id: "governing-law",
      title: "Governing Law & Jurisdiction",
      content: (
        <>
          <p>
            These GTC are governed by the laws of <strong>England and Wales</strong>.
          </p>
          <p>
            The courts of England and Wales have exclusive jurisdiction.
          </p>
        </>
      )
    },
    {
      id: "contact",
      title: "Contact",
      content: (
        <>
          <p>For any questions regarding these Terms:</p>
          <ul>
            <li><strong>Email:</strong> <a href="mailto:legal@visainterviews.ai" className="text-lime-600 dark:text-lime-400 hover:underline">legal@visainterviews.ai</a></li>
            <li><strong>Address:</strong> 4 Crown Place, Liverpool Street, London, EC2A 4BT</li>
          </ul>
        </>
      )
    }
  ];

  return (
    <LegalLayout 
      title="Terms and Conditions"
      effectiveDate="1 August 2025"
      lastUpdated="31 August 2025"
      sections={sections}
      icon={FileText}
    />
  );
}
